import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Shield, Database, Lock, EyeOff, Share2, Cookie, UserCheck, Mail, FileText } from 'lucide-react';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Privacy Policy"
          description="Situs Canada privacy policy. How we collect, use, and protect your data. 100% Canadian data residency, PIPEDA compliant, zero third-party data sharing."
          canonical="https://situsai.ca/privacy"
        />
        <JsonLd data={createBreadcrumbSchema('/privacy')} />

        {/* Hero Section */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <Shield className="w-4 h-4" /> Your Privacy Matters
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Privacy Policy for<br className="hidden sm:block" /> Situs AI and Orca AI
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto mb-10">
            We are committed to protecting your personal information and being transparent about how we collect, use, and safeguard your data.
          </p>
          <p className="text-sm text-[var(--color-csai-muted)]">
            <strong>Effective Date:</strong> March 19, 2026
          </p>
        </section>

        {/* Table of Contents */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-6 mb-8">
            <h2 className="text-lg font-semibold text-[var(--color-csai-accent)] mb-4">Table of Contents</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[var(--color-csai-muted)]">
              <li><a href="#introduction" className="hover:text-[var(--color-csai-primary)]">1. Introduction</a></li>
              <li><a href="#collection" className="hover:text-[var(--color-csai-primary)]">2. Data Collection</a></li>
              <li><a href="#storage" className="hover:text-[var(--color-csai-primary)]">3. Data Storage</a></li>
              <li><a href="#usage" className="hover:text-[var(--color-csai-primary)]">4. Data Usage</a></li>
              <li><a href="#sharing" className="hover:text-[var(--color-csai-primary)]">5. Data Sharing</a></li>
              <li><a href="#cookies" className="hover:text-[var(--color-csai-primary)]">6. Cookies</a></li>
              <li><a href="#rights" className="hover:text-[var(--color-csai-primary)]">7. Your Rights</a></li>
              <li><a href="#contact" className="hover:text-[var(--color-csai-primary)]">8. Contact</a></li>
            </ul>
          </div>
        </section>

        {/* Content Sections */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8">
          {/* 1. Introduction */}
          <div id="introduction" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">1. Introduction</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed">
              Situs AI ("we," "our," or "us") operates the Orca AI platform, a Canadian AI assistant designed for professional practices including accounting firms, law offices, and financial institutions. This Privacy Policy explains how we collect, use, disclose, and protect your personal information in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws.
            </p>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mt-4">
              By using Orca AI, you consent to the collection and use of your information as described in this policy. If you do not agree with our practices, please do not use our service.
            </p>
          </div>

          {/* 2. Data Collection */}
          <div id="collection" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Database className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">2. Data Collection</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              We collect the following types of information to provide and improve our AI-powered services:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Chat Inputs:</strong> The questions, prompts, and instructions you submit through Orca AI</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Uploaded Documents:</strong> Files you upload for analysis, including financial statements, legal documents, and other professional records</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Usage Data:</strong> Information about how you interact with the service, including features used, query patterns, and session duration</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Account Information:</strong> Your name, email address, organization, and billing information when you create an account</span>
              </li>
            </ul>
          </div>

          {/* 3. Data Storage */}
          <div id="storage" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">3. Data Storage</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              <strong>100% Canadian Data Residency:</strong> All data collected through Orca AI is stored exclusively in our Montreal, Quebec data centers. Your data never leaves Canadian borders.
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Data encrypted at rest using AES-256 encryption</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Data encrypted in transit using TLS 1.3</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Isolated storage vaults prevent cross-client data access</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Complete immunity from US CLOUD Act and foreign surveillance</span>
              </li>
            </ul>
          </div>

          {/* 4. Data Usage */}
          <div id="usage" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <EyeOff className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">4. Data Usage</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              We use your data <strong>only</strong> to provide the Orca AI service. Specifically:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>To process your queries and provide AI-generated responses</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>To store and retrieve your documents within your private vault</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>To maintain and improve service functionality</span>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-red-800 font-semibold text-sm">
                Zero Data Training Policy
              </p>
              <p className="text-red-700 text-sm mt-1">
                Your prompts, uploaded documents, and chat histories are <strong>never</strong> used to train, fine-tune, or improve our AI models. This is a binding commitment, not a option.
              </p>
            </div>
          </div>

          {/* 5. Data Sharing */}
          <div id="sharing" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Share2 className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">5. Data Sharing</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              We do not sell, rent, or share your personal information with third parties except in the following circumstances:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Required by Law:</strong> We may disclose information if required to comply with valid legal requests, court orders, or governmental investigations</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Service Providers:</strong> We use trusted third-party service providers for payment processing and infrastructure. These providers are bound by confidentiality agreements</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>With Your Consent:</strong> We will never share your data for marketing purposes without your explicit consent</span>
              </li>
            </ul>
          </div>

          {/* 6. Cookies */}
          <div id="cookies" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Cookie className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">6. Cookies</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              We use minimal cookies to ensure the functionality of our platform:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Essential Cookies:</strong> Required for authentication and security (session cookies)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span><strong>Analytics Cookies:</strong> We use anonymized, aggregated analytics to understand usage patterns and improve the service. These cookies do not identify individual users</span>
              </li>
            </ul>
            <p className="text-[var(--color-csai-muted)] text-sm mt-4">
              You can disable cookies in your browser settings, but some features of Orca AI may not function properly.
            </p>
          </div>

          {/* 7. Your Rights */}
          <div id="rights" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">7. Your Rights Under PIPEDA</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              As a Canadian user of Orca AI, you have the following rights under PIPEDA:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Right to Access:</strong> Request a copy of your personal information held by us</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Right to Correction:</strong> Request that we correct any inaccurate or incomplete personal information</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Right to Withdrawal:</strong> Withdraw consent for data collection (subject to legal requirements)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Right to Deletion:</strong> Request deletion of your account and associated data, subject to legal retention requirements</span>
              </li>
            </ul>
          </div>

          {/* 8. Contact */}
          <div id="contact" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">8. Contact Us</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact our Privacy Officer:
            </p>
            <div className="bg-[var(--color-csai-surface)] rounded-xl p-6 space-y-2 text-[var(--color-csai-muted)]">
              <p className="font-semibold text-[var(--color-csai-accent)]">Situs AI Inc.</p>
              <p>Privacy Officer</p>
              <p>Montreal, Quebec, Canada</p>
              <p>Email: <a href="mailto:info@situsai.ca" className="text-[var(--color-csai-primary)] hover:underline">info@situsai.ca</a></p>
            </div>
            <p className="text-[var(--color-csai-muted)] text-sm mt-4">
              We will respond to all privacy-related inquiries within 30 days of receipt.
            </p>
            <p className="text-[var(--color-csai-muted)] text-sm mt-4">
              For a list of third-party sub-processors who handle your data, see our <a href="/subprocessors" className="text-[var(--color-csai-primary)] hover:underline">Sub-processors page</a>.
            </p>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <Link to="/">
            <Button variant="secondary" className="text-lg px-8">Back to Home</Button>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
